import {
    Box,
    Typography,
    Avatar,
    Tooltip,
    Chip,
    Modal,
    IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import TableContainer from "../../Components/TableContainer";
import DummyUser from "../../Assets/User.webp";
import CloseIcon from "@mui/icons-material/Close";

const ReportedComments = () => {
    const pageSize = 15;
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reportedCommentsData, setReportedCommentsData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState("");

    const getReportedCommentsList = async () => {
        setLoading(true);
        try {
            const skip = page * pageSize;
            const params = {
                select: "cr.id, cr.comment_id, cr.created_at, cr.status, u.username AS report_by_username, u.image AS report_by_user_image, fc.comment, crr.reason, cm_own.username AS comment_own_username, cm_own.image AS comment_own_image",
                from: "comment_reports AS cr JOIN feed_comments AS fc ON fc.id = cr.comment_id JOIN comment_report_reasons AS crr ON crr.id = cr.comment_report_reasons_id JOIN users AS cm_own ON cm_own.id = fc.user_id JOIN users AS u ON u.id = cr.reported_by",
                arg: "cr.status = 1 AND fc.status = 1 AND crr.status = 1 AND cm_own.status = 1 AND u.status = 1 ORDER BY cr.created_at DESC",
                skip: skip,
                limit: pageSize,
                total: 1,
            };
            const response = await AxiosInstance.post(ApiList.USER_GET, params);
            if (response?.data?.s === 1 && response?.data?.r?.length > 0) {
                const formattedReports = response?.data?.r?.map((report, index) => ({
                    rowid: index + 1 + page * pageSize,
                    ...report,
                }));
                setReportedCommentsData(formattedReports);
                if (skip === 0) {
                    const totalCount = response?.data?.total || 0;
                    localStorage.setItem("reportedCommentsTotalCount", totalCount);
                    setTotalData(totalCount);
                } else {
                    const totalCountFromStorage = localStorage.getItem("reportedCommentsTotalCount");
                    setTotalData(totalCountFromStorage ? Number(totalCountFromStorage) : 0);
                }
            } else {
                // No data from API
                setReportedCommentsData([]);
                setTotalData(0);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching reported comments data:", error);
            // No data on error
            setReportedCommentsData([]);
            setTotalData(0);
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleOpenModal = (mediaUrl) => {
        setSelectedMedia(mediaUrl);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedMedia("");
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    useEffect(() => {
        getReportedCommentsList();
    }, [page]);

    const columns = [
        {
            field: "rowid",
            headerName: "Sr.No",
            width: 80,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "report_by_user_image",
            headerName: "Reporter",
            width: 100,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Avatar
                    sx={{
                        width: "40px",
                        height: "40px",
                        objectFit: "contain",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <img
                        src={
                            params?.row?.report_by_user_image
                                ? `${ApiList?.IMG_URL}${params?.row?.report_by_user_image}`
                                : DummyUser
                        }
                        style={{ width: "100%", height: "100%" }}
                        alt="Reporter"
                    />
                </Avatar>
            ),
        },
        {
            field: "report_by_username",
            headerName: "Reporter Name",
            flex: 1,
            minWidth: 150,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const username = params?.row?.report_by_username;
                const truncatedUsername =
                    username?.length > 20 ? `${username.substring(0, 20)}...` : username;
                return (
                    <Tooltip title={username}>
                        {truncatedUsername}
                    </Tooltip>
                );
            },
        },
        {
            field: "comment",
            headerName: "Comment",
            flex: 1,
            minWidth: 200,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const comment = params?.row?.comment;
                const truncatedComment =
                    comment?.length > 30 ? `${comment.substring(0, 30)}...` : comment;
                return (
                    <Tooltip title={comment}>
                        {truncatedComment}
                    </Tooltip>
                );
            },
        },
        {
            field: "reason",
            headerName: "Report Reason",
            flex: 1,
            minWidth: 150,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const reason = params?.row?.reason;
                const truncatedReason =
                    reason?.length > 25 ? `${reason.substring(0, 25)}...` : reason;
                return (
                    <Tooltip title={reason}>
                        {truncatedReason}
                    </Tooltip>
                );
            },
        },
        {
            field: "comment_own_username",
            headerName: "Comment Owner",
            flex: 1,
            minWidth: 150,
            headerClassName: "leftAlignHeader",
            cellClassName: "leftAlign",
            renderCell: (params) => {
                const username = params?.row?.comment_own_username;
                const truncatedUsername =
                    username?.length > 20 ? `${username.substring(0, 20)}...` : username;
                return (
                    <Tooltip title={username}>
                        {truncatedUsername}
                    </Tooltip>
                );
            },
        },
        
        {
            field: "created_at",
            headerName: "Reported Date",
            width: 150,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Typography sx={{ fontSize: "12px" }}>
                    {formatDate(params?.row?.created_at)}
                </Typography>
            ),
        },
    ];

    return (
        <>
            <Box
                sx={{
                    width: "100%",
                    height: "100vh",
                    background: "#B9CC0310",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ width: "100%", p: 2, background: "#B9CC0310" }}>
                    <Typography
                        sx={{
                            fontSize: { xs: "18px", md: "28px" },
                            fontWeight: "300",
                            color: "#000",
                        }}
                    >
                        Reported Comments
                    </Typography>
                </Box>
                <Box sx={{ width: "100%", height: "calc(100vh - 74px)" }}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            p: 1,
                            overflow: "scroll",
                        }}
                    >
                        <TableContainer
                            columns={columns}
                            rows={reportedCommentsData}
                            handlePageChange={handlePageChange}
                            page={page}
                            loading={loading}
                            totalData={totalData}
                            pageSize={pageSize}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Modal for Media */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                BackdropProps={{
                    sx: {
                        backdropFilter: "blur(5px)",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "400px",
                        height: "400px",
                        bgcolor: "none",
                        p: 2,
                        borderRadius: 2,
                        outline: "none",
                    }}
                >
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                        }}
                    >
                        <CloseIcon sx={{ color: "white" }} />
                    </IconButton>
                    <img
                        src={selectedMedia}
                        alt="Media Preview"
                        style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "8px",
                            objectFit: "contain",
                        }}
                    />
                </Box>
            </Modal>
        </>
    );
};

export default ReportedComments;